import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faDownload,
  faUpload,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { secureFetch } from '../../utils/secureFetch';
import { isEmbedded } from '../../utils/isEmbedded';
import './ProductBulkEditModal.css';

function ProductBulkEditModal({ isOpen, onClose, onEditComplete }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [activeTab, setActiveTab] = useState('correct');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      setUploadedFile(null);
      setValidationResult(null);
      setActiveTab('correct');
      setIsUpdating(false);
      setUpdateProgress(null);
      setErrorMessage('');
      setSuccessMessage('');

      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isUpdating) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isUpdating, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isUpdating) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      if (!validTypes.includes(file.type)) {
        setErrorMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
        return;
      }

      setUploadedFile(file);
      setValidationResult(null);
      setErrorMessage('');
      setSuccessMessage('');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadProducts = async () => {
    try {
      setIsDownloading(true);
      setErrorMessage('');

      const user = JSON.parse(localStorage.getItem('usuario'));
      const providerId = user?.proveedorInfo?.id;

      if (!providerId) {
        throw new Error('No se encontró información del proveedor');
      }

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded()
        ? `https://api.minymol.com/bulk-import/catalog/export/${providerId}`
        : `https://api.minymol.com/bulk-import/export/${providerId}`;

      const response = await secureFetch(endpoint, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Error al exportar productos');
      }

      const result = await response.json();

      // Convert base64 to blob
      const byteCharacters = atob(result.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || 'mis_productos.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Productos exportados exitosamente');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error downloading products:', error);
      setErrorMessage('Error al exportar productos. Por favor intenta nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleValidateFile = async () => {
    if (!uploadedFile) return;

    try {
      setIsValidating(true);
      setErrorMessage('');
      setValidationResult(null);

      const formData = new FormData();
      formData.append('file', uploadedFile);

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded()
        ? 'https://api.minymol.com/bulk-import/catalog/validate-update'
        : 'https://api.minymol.com/bulk-import/validate-update';

      const response = await secureFetch(endpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al validar el archivo');
      }

      const result = await response.json();
      setValidationResult(result);

      if (result.errors && result.errors.length > 0) {
        setActiveTab('errors');
        setErrorMessage(`Se encontraron ${result.errors.length} error(es) en el archivo`);
      } else {
        setActiveTab('correct');
        setSuccessMessage(`Validación exitosa: ${result.validProducts.length} producto(s) listo(s) para actualizar`);
      }
    } catch (error) {
      console.error('Error validating file:', error);
      setErrorMessage(error.message || 'Error al validar el archivo. Por favor intenta nuevamente.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleUpdateProducts = async () => {
    if (!validationResult || !validationResult.validProducts || validationResult.validProducts.length === 0) {
      return;
    }

    try {
      setIsUpdating(true);
      setErrorMessage('');
      setUpdateProgress({
        current: 0,
        total: validationResult.validProducts.length,
        success: 0,
        failed: 0
      });

      // Use catalog endpoint if embedded, regular endpoint otherwise
      const endpoint = isEmbedded()
        ? 'https://api.minymol.com/bulk-import/catalog/update'
        : 'https://api.minymol.com/bulk-import/update';

      const response = await secureFetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          validProducts: validationResult.validProducts
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar productos');
      }

      const result = await response.json();

      const successCount = result.successCount || 0;
      const failedCount = result.failureCount || 0;
      const totalCount = result.totalProcessed || validationResult.validProducts.length;

      setUpdateProgress({
        current: totalCount,
        total: totalCount,
        success: successCount,
        failed: failedCount
      });

      if (failedCount > 0) {
        setErrorMessage(`Actualización completada con ${failedCount} error(es). ${successCount} producto(s) actualizado(s) exitosamente.`);
      } else {
        setSuccessMessage(`¡Actualización exitosa! ${successCount} producto(s) actualizado(s).`);
      }

      if (onEditComplete) {
        onEditComplete();
      }

      setTimeout(() => {
        if (failedCount === 0) {
          onClose();
        }
      }, 2000);

    } catch (error) {
      console.error('Error updating products:', error);
      setErrorMessage(error.message || 'Error al actualizar productos. Por favor intenta nuevamente.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="product-bulk-edit-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-bulk-edit-modal-title"
    >
      <div className="product-bulk-edit-modal-backdrop" />

      <div className="product-bulk-edit-modal-container">
        <div
          ref={modalRef}
          className="product-bulk-edit-modal-content"
          tabIndex="-1"
        >
          {/* Header */}
          <div className="product-bulk-edit-modal-header">
            <h2 id="product-bulk-edit-modal-title">
              Editar Productos Masivamente
            </h2>
            <button
              onClick={handleClose}
              disabled={isUpdating}
              className="product-bulk-edit-modal-close"
              aria-label="Cerrar modal"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Content */}
          <div className="product-bulk-edit-modal-body">
            {/* Success Message */}
            {successMessage && (
              <div className="product-bulk-edit-alert product-bulk-edit-alert-success">
                <FontAwesomeIcon icon={faCheckCircle} />
                <p>{successMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="product-bulk-edit-alert product-bulk-edit-alert-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="product-bulk-edit-instructions">
              <h3>Instrucciones:</h3>
              <ol>
                <li>Descarga el archivo Excel con todos tus productos actuales</li>
                <li>Edita los campos que desees modificar (nombre, descripción, precios, etc.)</li>
                <li>Guarda el archivo y súbelo aquí para validar los cambios</li>
                <li>Revisa los cambios y confirma la actualización</li>
              </ol>
            </div>

            {/* Download Products Button */}
            <div className="product-bulk-edit-section">
              <button
                onClick={handleDownloadProducts}
                disabled={isUpdating || isValidating || isDownloading}
                className="product-bulk-edit-btn product-bulk-edit-btn-primary"
              >
                {isDownloading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>Descargando productos...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Descargar Mis Productos</span>
                  </>
                )}
              </button>
            </div>

            {/* File Upload Area */}
            <div className="product-bulk-edit-section">
              <label className="product-bulk-edit-label">
                Subir archivo Excel editado
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUpdating || isValidating}
              />
              <div
                onClick={handleUploadClick}
                className={`product-bulk-edit-upload-area ${uploadedFile ? 'has-file' : ''} ${(isUpdating || isValidating) ? 'disabled' : ''}`}
              >
                <FontAwesomeIcon
                  icon={uploadedFile ? faCheckCircle : faUpload}
                  className="upload-icon"
                />
                {uploadedFile ? (
                  <div>
                    <p className="font-medium">
                      Archivo seleccionado: {uploadedFile.name}
                    </p>
                    <p className="text-sm">
                      Haz clic para seleccionar otro archivo
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">
                      Haz clic para seleccionar un archivo
                    </p>
                    <p className="text-sm">
                      o arrastra y suelta aquí
                    </p>
                    <p className="text-xs">
                      Formatos soportados: .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Validate Button */}
            {uploadedFile && !validationResult && (
              <div className="product-bulk-edit-section">
                <button
                  onClick={handleValidateFile}
                  disabled={isUpdating || isValidating}
                  className="product-bulk-edit-btn product-bulk-edit-btn-secondary"
                >
                  {isValidating ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>Validando archivo...</span>
                    </>
                  ) : (
                    <span>Validar Cambios</span>
                  )}
                </button>
              </div>
            )}

            {/* Validation Results */}
            {validationResult && (
              <div className="product-bulk-edit-section">
                {/* Tabs */}
                <div className="product-bulk-edit-tabs">
                  <button
                    onClick={() => setActiveTab('correct')}
                    className={`product-bulk-edit-tab ${activeTab === 'correct' ? 'active' : ''}`}
                  >
                    Correctos ({validationResult.validProducts?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('errors')}
                    className={`product-bulk-edit-tab ${activeTab === 'errors' ? 'active' : ''}`}
                  >
                    Errores ({validationResult.errors?.length || 0})
                  </button>
                </div>

                {/* Tab Content */}
                <div className="product-bulk-edit-tab-content">
                  {activeTab === 'correct' && (
                    <div className="product-bulk-edit-table-wrapper">
                      {validationResult.validProducts && validationResult.validProducts.length > 0 ? (
                        <>
                          {/* Warning for products that will have variants removed */}
                          {validationResult.validProducts.some(p => p.colors?.length === 0 || p.sizes?.length === 0) && (
                            <div className="product-bulk-edit-alert product-bulk-edit-alert-warning" style={{ marginBottom: '1rem' }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                              <div>
                                <p className="font-medium">Atención: Eliminación de variantes</p>
                                <p className="text-sm">
                                  Algunos productos tienen las columnas Color y/o Talla vacías. 
                                  Al actualizar, se eliminarán todas las variantes de estos productos.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <table className="product-bulk-edit-table">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Precio</th>
                                <th>Estado</th>
                                <th>Variantes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validationResult.validProducts.map((product, index) => {
                                const willRemoveVariants = (product.colors?.length === 0 || product.sizes?.length === 0) && 
                                                          (product.colors !== undefined || product.sizes !== undefined);
                                const hasVariants = (product.colors && product.colors.length > 0) || 
                                                   (product.sizes && product.sizes.length > 0);
                                
                                return (
                                  <tr key={index} className={willRemoveVariants ? 'variant-warning-row' : ''}>
                                    <td className="font-mono text-sm">{product.id}</td>
                                    <td>{product.name}</td>
                                    <td>${product.prices?.[0]?.price?.toLocaleString() || 'N/A'}</td>
                                    <td>
                                      <span className="status-badge">
                                        {product.status || 'active'}
                                      </span>
                                    </td>
                                    <td>
                                      {willRemoveVariants ? (
                                        <span className="variant-warning-badge">
                                          <FontAwesomeIcon icon={faExclamationTriangle} />
                                          Se eliminarán variantes
                                        </span>
                                      ) : hasVariants ? (
                                        <span className="variant-info-badge">
                                          {product.colors?.length || 0} color(es), {product.sizes?.length || 0} talla(s)
                                        </span>
                                      ) : (
                                        <span className="variant-info-badge">Sin cambios</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <div className="product-bulk-edit-empty">
                          No hay productos válidos
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'errors' && (
                    <div className="product-bulk-edit-table-wrapper">
                      {validationResult.errors && validationResult.errors.length > 0 ? (
                        <table className="product-bulk-edit-table">
                          <thead>
                            <tr>
                              <th>Fila</th>
                              <th>Columna</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResult.errors.map((error, index) => (
                              <tr key={index}>
                                <td className="font-medium">{error.row}</td>
                                <td>{error.column}</td>
                                <td className="text-danger">{error.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="product-bulk-edit-empty product-bulk-edit-empty-success">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <p>¡No se encontraron errores!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="product-bulk-edit-modal-footer">
            {updateProgress && (
              <div className="product-bulk-edit-progress">
                <div className="product-bulk-edit-progress-info">
                  <span>Progreso: {updateProgress.current} / {updateProgress.total}</span>
                  <span>Exitosos: {updateProgress.success} | Fallidos: {updateProgress.failed}</span>
                </div>
                <div className="product-bulk-edit-progress-bar">
                  <div
                    className="product-bulk-edit-progress-fill"
                    style={{
                      width: `${(updateProgress.current / updateProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}

            {!updateProgress && (
              <div className="product-bulk-edit-actions">
                <button
                  onClick={handleClose}
                  disabled={isUpdating}
                  className="product-bulk-edit-btn product-bulk-edit-btn-cancel"
                >
                  Cancelar
                </button>

                {validationResult && validationResult.validProducts && validationResult.validProducts.length > 0 && (
                  <button
                    onClick={handleUpdateProducts}
                    disabled={
                      isUpdating ||
                      isValidating ||
                      (validationResult.errors && validationResult.errors.length > 0)
                    }
                    className="product-bulk-edit-btn product-bulk-edit-btn-success"
                  >
                    {isUpdating ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>Actualizando...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>Actualizar {validationResult.validProducts.length} Producto(s)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductBulkEditModal;
